import { documentComponentFromData } from "./DocumentComponentRegistry";
import { newRandomHexidecimalKey } from "./utils";

class IsibuteDocument {
  constructor(data) {
    this.id = data?.document?.id;
    this.documentManagerId = data?.document?.documentManagerId;
    this.author = data?.document?.author;
    this.publishedAt = data?.document?.publishedAt;
    this.documentComponents = {};
    this.cursorStartPosition = 1;
    this.cursorEndPosition = 1;
    this.currentStartNodeCursorPosition = 1;
    this.currentEndNodeCursorPosition = 1;
    this.pendingStyleTypes = null;

    if (data?.document?.documentComponents) {
      Object.entries(data?.document?.documentComponents).forEach(([key, value]) => {
        this.documentComponents[key] = documentComponentFromData({ ...value, isibuteDocument: this });
      });
    }

    // Ensure document always has at least one component for editing to work.
    // Skip this when leaveEmptyOnConstruction is true (e.g., during deepCopy).
    const leaveEmptyOnConstruction = data?.leaveEmptyOnConstruction === true;
    if (!leaveEmptyOnConstruction && Object.keys(this.documentComponents).length === 0) {
      const defaultComponentId = newRandomHexidecimalKey();
      this.documentComponents[defaultComponentId] = documentComponentFromData({
        id: defaultComponentId,
        unstyledContent: "\u200b",
        styling: [{ start: 0, end: 1, types: ["none"] }],
        ordinal: 1,
        documentComponentType: "DocumentParagraph",
        parentDocumentComponentId: null,
        firstChildDocumentComponentId: null,
        lastChildDocumentComponentId: null,
        previousDocumentComponentId: null,
        nextDocumentComponentId: null,
        isibuteDocument: this,
      });
    }

    const firstComponent = Object.values(this.documentComponents).find(c => c.ordinal === 1) || null;
    this.currentStartNode = firstComponent;
    this.currentEndNode = firstComponent;
  }

  // Returns the collection (object keyed by id) that contains the given node.
  // For child components (citations, unordered list items, contact header children),
  // this is the parent's children collection. For top-level components, it's
  // this.documentComponents.
  getCollectionContainingNode = (node) => {
    if (node.parentDocumentComponentId) {
      const parent = this.documentComponents[node.parentDocumentComponentId];
      if (parent) {
        if (parent.documentCitations) return parent.documentCitations;
        if (parent.documentUnorderedListItems) return parent.documentUnorderedListItems;
        if (parent.contactHeaderChildren) return parent.contactHeaderChildren;
      }
    }
    return this.documentComponents;
  };

  // documentComponents are stored as an object (keyed by id) for O(1) lookup during editing,
  // but serialized as an array for Rails/ActiveRecord which expects arrays of records.
  // Ordering is preserved via ordinal and linked-list pointers on each component.
  asJSON = () => {
    return {
      id: this.id,
      documentManagerId: this.documentManagerId,
      author: this.author,
      publishedAt: this.publishedAt,
      documentComponents: Object.values(this.documentComponents).map((documentComponent) => {
        return documentComponent.asJSON();
      }),
    };
  };

  getDocumentComponentByKey = (key) => {
    return this.documentComponents[key] || null;
  };

  getDocumentComponentByKeyPath = ({ keyPath }) => {
    const [first, ...rest] = keyPath ?? [];
    const currentDocumentComponent = this.getDocumentComponentByKey(first);

    if (rest.length > 0) {
      return currentDocumentComponent?.getDocumentComponentByKeyPath({ keyPath: rest }) || null;
    }

    return currentDocumentComponent;
  };

  guaranteedNewRandomHexidecimalKey = () => {
    let newKey = newRandomHexidecimalKey();

    while (Object.keys(this.documentComponents).includes(newKey)) {
      newKey = newRandomHexidecimalKey();
    }

    return newKey;
  };

  setEndOfRangeToStart = () => {
    this.cursorEndPosition = this.cursorStartPosition;
    this.currentEndNodeCursorPosition = this.currentStartNodeCursorPosition;
    this.currentEndNode = this.currentStartNode;
  };

  setStartOfRangeToEnd = () => {
    this.cursorStartPosition = this.cursorEndPosition;
    this.currentStartNodeCursorPosition = this.currentEndNodeCursorPosition;
    this.currentStartNode = this.currentEndNode;
  };

  arrowLeft = () => {
    if (this.cursorStartPosition == this.cursorEndPosition) {
      if (this.currentStartNodeCursorPosition > 1) {
        this.cursorStartPosition = this.cursorStartPosition - 1;
        this.currentStartNodeCursorPosition = this.currentStartNodeCursorPosition - 1;
        this.setEndOfRangeToStart();
      } else {
        const componentForArrowLeft = this.currentStartNode.getComponentForArrowLeft();

        if (componentForArrowLeft) {
          this.cursorStartPosition = this.cursorStartPosition - 1;
          this.currentStartNodeCursorPosition = componentForArrowLeft.unstyledContent.length;
          this.currentStartNode = componentForArrowLeft;
          this.setEndOfRangeToStart();
        }
      }
    } else {
      this.setEndOfRangeToStart();
    }
  };

  arrowRight = () => {
    if (this.cursorStartPosition == this.cursorEndPosition) {
      if (this.currentEndNodeCursorPosition < this.currentEndNode.unstyledContent.length) {
        this.cursorEndPosition = this.cursorEndPosition + 1;
        this.currentEndNodeCursorPosition = this.currentEndNodeCursorPosition + 1;
        this.setStartOfRangeToEnd();
      } else {
        const componentForArrowRight = this.currentEndNode.getComponentForArrowRight();

        if (componentForArrowRight) {
          this.cursorEndPosition = this.cursorEndPosition + 1;
          this.currentEndNodeCursorPosition = 1;
          this.currentEndNode = componentForArrowRight;
          this.setStartOfRangeToEnd();
        }
      }
    } else {
      this.setStartOfRangeToEnd();
    }
  };

  backspace = () => {
    if (this.cursorStartPosition == this.cursorEndPosition) {
      this.backspaceSingleCharacterFromEnd();

      this.setStartOfRangeToEnd();
    } else {
      this.backspaceRange();
    }
  };

  backspaceRange = () => {
    while (this.cursorStartPosition < this.cursorEndPosition) {
      this.backspaceSingleCharacterFromEnd();
    }
  }

  backspaceSingleCharacterFromEnd = () => {
    if (this.cursorEndPosition <= 1) {
      return null;
    }

    const newStylingAfterBackspace = () => {
      return this.currentEndNode.styling.map((oldStyle) => {
        let newStyle = { types: oldStyle.types };
        if (oldStyle.href !== undefined) newStyle.href = oldStyle.href;

        if (oldStyle.start < this.currentEndNodeCursorPosition && oldStyle.end < this.currentEndNodeCursorPosition) {
          newStyle.start = oldStyle.start;
          newStyle.end = oldStyle.end;
        }

        if (oldStyle.start < this.currentEndNodeCursorPosition && oldStyle.end == this.currentEndNodeCursorPosition) {
          newStyle.start = oldStyle.start;
          newStyle.end = oldStyle.end - 1;
        }

        if (oldStyle.start < this.currentEndNodeCursorPosition && this.currentEndNodeCursorPosition < oldStyle.end) {
          newStyle.start = oldStyle.start;
          newStyle.end = oldStyle.end - 1;
        }

        if (oldStyle.start == this.currentEndNodeCursorPosition && this.currentEndNodeCursorPosition < oldStyle.end) {
          newStyle.start = oldStyle.start;
          newStyle.end = oldStyle.end - 1;
        }

        if (oldStyle.start == this.currentEndNodeCursorPosition && this.currentEndNodeCursorPosition == oldStyle.end) {
          newStyle = null;
        }

        if (this.currentEndNodeCursorPosition < oldStyle.start && this.currentEndNodeCursorPosition < oldStyle.end) {
          newStyle.start = oldStyle.start - 1;
          newStyle.end = oldStyle.end - 1;
        }

        return newStyle;
      }).filter((el) => {
        return el != null;
      });
    };

    if (this.currentEndNodeCursorPosition > 1) {
      const newValueForCurrentNode =
        this.currentEndNode.unstyledContent.substring(0, this.currentEndNodeCursorPosition - 1) +
        this.currentEndNode.unstyledContent.substring(this.currentEndNodeCursorPosition);
      this.currentEndNode.unstyledContent = newValueForCurrentNode;
      this.currentEndNode.styling = newStylingAfterBackspace();
      this.cursorEndPosition = this.cursorEndPosition - 1;
      this.currentEndNodeCursorPosition = this.currentEndNodeCursorPosition - 1;
    } else {
      const componentForBackspace = this.currentEndNode.getComponentForArrowLeft();

      if (componentForBackspace) {
        const collection = this.getCollectionContainingNode(this.currentEndNode);

        Object.values(collection).forEach((documentComponent) => {
          if (documentComponent.ordinal > this.currentEndNode.ordinal) {
            documentComponent.ordinal = documentComponent.ordinal - 1;
          }
        });

        const stylingAfterJoin = this.currentEndNode.joinStyling({
          firstHalfOfStyling: componentForBackspace.styling,
          secondHalfOfStyling: this.currentEndNode.styling,
        });

        const unstyledContentAfterJoin = this.currentEndNode.joinUnstyledContent({
          firstHalfOfUnstyledConent: componentForBackspace.unstyledContent,
          secondHalfOfUnstyledContent: this.currentEndNode.unstyledContent,
        });

        const componentForBackspaceUnstyledContentLengthBeforeJoin = componentForBackspace.unstyledContent.length;

        collection[this.currentEndNode.previousDocumentComponentId].unstyledContent = unstyledContentAfterJoin;
        collection[this.currentEndNode.previousDocumentComponentId].styling = stylingAfterJoin;
        collection[this.currentEndNode.previousDocumentComponentId].nextDocumentComponentId = this.currentEndNode.nextDocumentComponentId;
        if (this.currentEndNode.nextDocumentComponentId) {
          collection[this.currentEndNode.nextDocumentComponentId].previousDocumentComponentId = this.currentEndNode.previousDocumentComponentId;
        }

        delete collection[this.currentEndNode.id];

        this.cursorEndPosition = this.cursorEndPosition - 1;
        this.currentEndNodeCursorPosition = componentForBackspaceUnstyledContentLengthBeforeJoin;
        this.currentEndNode = componentForBackspace;
      }
    }
  };

  enter = () => {
    this.backspaceRange();

    const typesRequiringParagraphAfter = ['DocumentTitle', 'DocumentSubtitle', 'DocumentEmphasis'];
    const newComponentType = typesRequiringParagraphAfter.includes(this.currentEndNode.documentComponentType)
      ? 'DocumentParagraph'
      : this.currentEndNode.documentComponentType;

    const newNode = documentComponentFromData({
      id: this.guaranteedNewRandomHexidecimalKey(),
      ordinal: this.currentEndNode.ordinal + 1,
      documentComponentType: newComponentType,
      isibuteDocument: this,
    });

    const unstyledContentForPreviousNode = this.currentEndNode.unstyledContent.substring(0, this.currentEndNodeCursorPosition);
    const unstyledContentForNewNodePrefix = '\u200b';
    const unstyledContentForNewNode = unstyledContentForNewNodePrefix + this.currentEndNode.unstyledContent.substring(this.currentEndNodeCursorPosition);

    newNode.unstyledContent = unstyledContentForNewNode;
    newNode.previousDocumentComponentId = this.currentEndNode.id;
    newNode.nextDocumentComponentId = this.currentEndNode.nextDocumentComponentId;
    newNode.parentDocumentComponentId = this.currentEndNode.parentDocumentComponentId;

    const enterCollection = this.getCollectionContainingNode(this.currentEndNode);

    Object.values(enterCollection).forEach((documentComponent) => {
      if (documentComponent.ordinal >= newNode.ordinal) {
        documentComponent.ordinal = documentComponent.ordinal + 1;
      }
    });

    const stylingsAfterSplit = this.currentEndNode.splitStyling({
      splitIndex: this.currentEndNodeCursorPosition
    });

    newNode.styling = stylingsAfterSplit.secondHalfOfStyling;

    const enterInsertCollection = this.getCollectionContainingNode(this.currentEndNode);
    enterInsertCollection[newNode.id] = newNode;
    enterInsertCollection[this.currentEndNode.id].unstyledContent = unstyledContentForPreviousNode;
    enterInsertCollection[this.currentEndNode.id].nextDocumentComponentId = newNode.id;
    enterInsertCollection[this.currentEndNode.id].styling = stylingsAfterSplit.firstHalfOfStyling;
    if (newNode.nextDocumentComponentId) {
      enterInsertCollection[newNode.nextDocumentComponentId].previousDocumentComponentId = newNode.id;
    }

    this.cursorEndPosition = this.cursorEndPosition + 1;
    this.currentEndNodeCursorPosition = 1;
    this.currentEndNode = newNode;

    this.setStartOfRangeToEnd();
  };

  addCharacter = (character) => {
    this.backspaceRange();

    const newStylingAfterNewChar = ({ stylingBeforeNewChar, cursorPositionBeforeNewChar }) => {
      return stylingBeforeNewChar.map((oldStyle) => {
        let newStyle = { types: oldStyle.types };
        if (oldStyle.href !== undefined) newStyle.href = oldStyle.href;

        if (oldStyle.start < cursorPositionBeforeNewChar && oldStyle.end < cursorPositionBeforeNewChar) {
          newStyle.start = oldStyle.start;
          newStyle.end = oldStyle.end;
        }

        if (oldStyle.start < cursorPositionBeforeNewChar && oldStyle.end == cursorPositionBeforeNewChar) {
          newStyle.start = oldStyle.start;
          newStyle.end = oldStyle.end + 1;
        }

        if (oldStyle.start < cursorPositionBeforeNewChar && cursorPositionBeforeNewChar < oldStyle.end) {
          newStyle.start = oldStyle.start;
          newStyle.end = oldStyle.end + 1;
        }

        if (oldStyle.start == cursorPositionBeforeNewChar && cursorPositionBeforeNewChar < oldStyle.end) {
          newStyle.start = oldStyle.start;
          newStyle.end = oldStyle.end + 1;
        }

        if (oldStyle.start == cursorPositionBeforeNewChar && cursorPositionBeforeNewChar == oldStyle.end) {
          newStyle.start = oldStyle.start;
          newStyle.end = oldStyle.end + 1;
        }

        if (cursorPositionBeforeNewChar < oldStyle.start && cursorPositionBeforeNewChar < oldStyle.end) {
          newStyle.start = oldStyle.start + 1;
          newStyle.end = oldStyle.end + 1;
        }

        return newStyle;
      }).filter((el) => {
        return el != null;
      });
    };

    const newValueForCurrentNode =
      this.currentEndNode.unstyledContent.substring(0, this.currentEndNodeCursorPosition) +
      character +
      this.currentEndNode.unstyledContent.substring(this.currentEndNodeCursorPosition);

    this.currentEndNode.unstyledContent = newValueForCurrentNode;
    this.currentEndNode.styling = newStylingAfterNewChar({
      stylingBeforeNewChar: this.currentEndNode.styling,
      cursorPositionBeforeNewChar: this.currentEndNodeCursorPosition - 1
    });

    this.cursorEndPosition = this.cursorEndPosition + 1;
    this.currentEndNodeCursorPosition = this.currentEndNodeCursorPosition + 1;

    this.setStartOfRangeToEnd();
  };

  getActiveStyleTypes = () => {
    if (!this.currentStartNode) return ['none'];

    if (this.cursorStartPosition === this.cursorEndPosition) {
      const position = Math.max(0, this.currentStartNodeCursorPosition - 1);
      return this.currentStartNode.getStyleTypesAtPosition(position);
    }

    let activeTypes = null;
    let styleCursorEndPosition = this.cursorEndPosition;
    let styleEndNodeCursorPosition = this.currentEndNodeCursorPosition - 1;
    let styleEndNode = this.currentEndNode;

    if (this.cursorEndPosition <= 1) return ['none'];
    if (styleCursorEndPosition <= this.cursorStartPosition) return ['none'];

    while (styleCursorEndPosition > this.cursorStartPosition) {
      if (this.currentStartNode.id === styleEndNode.id) {
        const typesInRange = styleEndNode.getStyleTypesInRange(
          this.currentStartNodeCursorPosition,
          styleEndNodeCursorPosition
        );
        activeTypes = activeTypes
          ? activeTypes.filter(t => typesInRange.includes(t))
          : typesInRange;
        break;
      }

      if (styleEndNodeCursorPosition > 0) {
        const typesInRange = styleEndNode.getStyleTypesInRange(0, styleEndNodeCursorPosition);
        activeTypes = activeTypes
          ? activeTypes.filter(t => typesInRange.includes(t))
          : typesInRange;

        const nextComponentBack = styleEndNode.getComponentForArrowLeft();
        styleCursorEndPosition -= styleEndNodeCursorPosition;
        styleEndNodeCursorPosition = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      } else {
        const nextComponentBack = styleEndNode.getComponentForArrowLeft();
        styleCursorEndPosition -= styleEndNodeCursorPosition;
        styleEndNodeCursorPosition = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      }
    }

    return activeTypes && activeTypes.length > 0 ? activeTypes : ['none'];
  };

  getActiveFormat = () => {
    if (!this.currentStartNode) return null;
    return this.currentStartNode.documentComponentType;
  };

  getActiveHref = () => {
    if (!this.currentStartNode) return null;
    const position = Math.max(0, this.currentStartNodeCursorPosition - 1);
    return this.currentStartNode.getHrefAtPosition(position);
  };

  applyFormat = (format) => {
    const currentType = this.currentStartNode?.documentComponentType;

    if (currentType === format.type && format.type !== 'DocumentParagraph' && format.type !== 'DocumentCitationsSection' && format.type !== 'DocumentUnorderedList') {
      if (format.type !== 'DocumentSectionTitle' || this.currentStartNode.size === format.size) {
        this.paragraphizeCurrentComponents();
        return;
      }
    }

    switch (format.type) {
      case 'DocumentTitle':
        this.titleizeCurrentComponent();
        break;
      case 'DocumentSubtitle':
        this.subtitleizeCurrentComponent();
        break;
      case 'DocumentParagraph':
        this.paragraphizeCurrentComponents();
        break;
      case 'DocumentSectionTitle':
        this.sectiontitleizeCurrentComponents(format);
        break;
      case 'DocumentEmphasis':
        this.emphasizeCurrentComponents();
        break;
      case 'DocumentCitationsSection':
        this.citationSectionizeCurrentComponents();
        break;
      case 'DocumentUnorderedList':
        this.unorderedListizeCurrentComponents();
        break;
      default:
        // NOOP
    }
  };

  titleizeCurrentComponent = () => {
    if (!this.currentStartNode) {
      throw new Error("Must select something to titleize.");
    }

    if (!this.currentEndNode) {
      throw new Error("Must select something to titleize.");
    }

    if (this.currentStartNode.parentDocumentComponentId) {
      throw new Error("Can only titleize first top-level component of document.");
    }

    if (this.currentStartNode.previousDocumentComponentId) {
      throw new Error("Can only titleize first top-level component of document.");
    }

    if (this.currentEndNode.previousDocumentComponentId) {
      throw new Error("Can only titleize first top-level component of document.");
    }

    if (this.currentEndNode.parentDocumentComponentId) {
      throw new Error("Can only titleize first top-level component of document.");
    }

    if (this.currentStartNode.id != this.currentEndNode.id) {
      throw new Error("Can only titleize one component of document at a time.");
    }

    const titleizedComponent = this.currentStartNode.titleizedCopy();

    this.currentStartNode = titleizedComponent;
    this.currentEndNode = titleizedComponent;
    this.documentComponents[titleizedComponent.id] = titleizedComponent;
  };

  subtitleizeCurrentComponent = () => {
    if (!this.currentStartNode) {
      throw new Error("Must select something to subtitleize.");
    }

    if (!this.currentEndNode) {
      throw new Error("Must select something to subtitleize.");
    }

    if (this.currentStartNode.id != this.currentEndNode.id) {
      throw new Error("Can only subtitleize one component of document at a time.");
    }

    const previousToCurrentComponent = this.currentStartNode.getComponentForArrowLeft();

    if(!previousToCurrentComponent) {
      throw new Error("Can only subtitleize second top-level component of document.");
    }

    if (previousToCurrentComponent.parentDocumentComponentId) {
      throw new Error("Can only subtitleize second top-level component of document.");
    }

    if (previousToCurrentComponent.previousDocumentComponentId) {
      throw new Error("Can only subtitleize second top-level component of document.");
    }

    const subtitleizedComponent = this.currentStartNode.subtitleizedCopy();

    this.currentStartNode = subtitleizedComponent;
    this.currentEndNode = subtitleizedComponent;
    this.documentComponents[subtitleizedComponent.id] = subtitleizedComponent;
  };

  paragraphizeCurrentComponents = () => {
    if (!this.currentStartNode) {
      throw new Error("Must select something to paragraphize.");
    }

    if (!this.currentEndNode) {
      throw new Error("Must select something to paragraphize.");
    }

    let styleCursorEndPosition = this.cursorEndPosition;
    let styleEndNodeCursorPostion = this.currentEndNodeCursorPosition - 1;
    let styleEndNode = this.currentEndNode;
    let nextComponentBack;
    let paragraphizedComponent;

    if (this.cursorEndPosition <= 1) {
      return null;
    }

    if (styleCursorEndPosition < this.cursorStartPosition) {
      return null;
    }

    while (styleCursorEndPosition >= this.cursorStartPosition) {
      if (this.currentStartNode.id == styleEndNode.id) {
        paragraphizedComponent = this.currentStartNode.paragraphizedCopy();

        this.currentStartNode = paragraphizedComponent;
        const paragraphizeSingleCollection = this.getCollectionContainingNode(styleEndNode);
        paragraphizeSingleCollection[paragraphizedComponent.id] = paragraphizedComponent;

        return null;
      }

      if (styleEndNodeCursorPostion > 0) {
        paragraphizedComponent = styleEndNode.paragraphizedCopy();

        const paragraphizeCollection = this.getCollectionContainingNode(styleEndNode);
        paragraphizeCollection[paragraphizedComponent.id] = paragraphizedComponent;

        nextComponentBack = styleEndNode.getComponentForArrowLeft();

        styleCursorEndPosition = styleCursorEndPosition - styleEndNodeCursorPostion;
        styleEndNodeCursorPostion = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      } else {
        nextComponentBack = styleEndNode.getComponentForArrowLeft();

        styleCursorEndPosition = styleCursorEndPosition - styleEndNodeCursorPostion;
        styleEndNodeCursorPostion = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      }
    }
  };

  sectiontitleizeCurrentComponents = (format) => {
    if (!this.currentStartNode) {
      throw new Error("Must select something to section titleize.");
    }

    if (!this.currentEndNode) {
      throw new Error("Must select something to section titleize.");
    }

    let styleCursorEndPosition = this.cursorEndPosition;
    let styleEndNodeCursorPostion = this.currentEndNodeCursorPosition - 1;
    let styleEndNode = this.currentEndNode;
    let nextComponentBack;
    let sectionTitleizedComponent;

    if (this.cursorEndPosition <= 1) {
      return null;
    }

    if (styleCursorEndPosition < this.cursorStartPosition) {
      return null;
    }

    while (styleCursorEndPosition >= this.cursorStartPosition) {
      if (this.currentStartNode.id == styleEndNode.id) {
        sectionTitleizedComponent = this.currentStartNode.sectionTitleizedCopy(format);

        this.currentStartNode = sectionTitleizedComponent;
        const sectionTitleizeSingleCollection = this.getCollectionContainingNode(styleEndNode);
        sectionTitleizeSingleCollection[sectionTitleizedComponent.id] = sectionTitleizedComponent;

        return null;
      }

      if (styleEndNodeCursorPostion > 0) {
        sectionTitleizedComponent = styleEndNode.sectionTitleizedCopy(format);

        const sectionTitleizeCollection = this.getCollectionContainingNode(styleEndNode);
        sectionTitleizeCollection[sectionTitleizedComponent.id] = sectionTitleizedComponent;

        nextComponentBack = styleEndNode.getComponentForArrowLeft();

        styleCursorEndPosition = styleCursorEndPosition - styleEndNodeCursorPostion;
        styleEndNodeCursorPostion = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      } else {
        nextComponentBack = styleEndNode.getComponentForArrowLeft();

        styleCursorEndPosition = styleCursorEndPosition - styleEndNodeCursorPostion;
        styleEndNodeCursorPostion = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      }
    }
  };

  emphasizeCurrentComponents = () => {
    if (!this.currentStartNode) {
      throw new Error("Must select something to emphasize.");
    }

    if (!this.currentEndNode) {
      throw new Error("Must select something to emphasize.");
    }

    let styleCursorEndPosition = this.cursorEndPosition;
    let styleEndNodeCursorPostion = this.currentEndNodeCursorPosition - 1;
    let styleEndNode = this.currentEndNode;
    let nextComponentBack;
    let emphasizedComponent;

    if (this.cursorEndPosition <= 1) {
      return null;
    }

    if (styleCursorEndPosition < this.cursorStartPosition) {
      return null;
    }

    while (styleCursorEndPosition >= this.cursorStartPosition) {
      if (this.currentStartNode.id == styleEndNode.id) {
        emphasizedComponent = this.currentStartNode.emphasizedCopy();

        this.currentStartNode = emphasizedComponent;
        const emphasizeSingleCollection = this.getCollectionContainingNode(styleEndNode);
        emphasizeSingleCollection[emphasizedComponent.id] = emphasizedComponent;

        return null;
      }

      if (styleEndNodeCursorPostion > 0) {
        emphasizedComponent = styleEndNode.emphasizedCopy();

        const emphasizeCollection = this.getCollectionContainingNode(styleEndNode);
        emphasizeCollection[emphasizedComponent.id] = emphasizedComponent;

        nextComponentBack = styleEndNode.getComponentForArrowLeft();

        styleCursorEndPosition = styleCursorEndPosition - styleEndNodeCursorPostion;
        styleEndNodeCursorPostion = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      } else {
        nextComponentBack = styleEndNode.getComponentForArrowLeft();

        styleCursorEndPosition = styleCursorEndPosition - styleEndNodeCursorPostion;
        styleEndNodeCursorPostion = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      }
    }
  };

  citationSectionizeCurrentComponents = () => {
    if (!this.currentStartNode) {
      throw new Error("Must select something to citation sectionize.");
    }

    if (!this.currentEndNode) {
      throw new Error("Must select something to citation sectionize.");
    }

    // Should add check that there are no Citation Section or Citation components between start and end node (inclusive)

    let styleCursorEndPosition = this.cursorEndPosition;
    let styleEndNodeCursorPostion = this.currentEndNodeCursorPosition - 1;
    let styleEndNode = this.currentEndNode;
    let nextComponentBack;
    const ordinalDifference = this.currentEndNode.ordinal - this.currentStartNode.ordinal;
    const previousComponentId = this.currentStartNode.previousDocumentComponentId;
    const nextComponentId = this.currentEndNode.nextDocumentComponentId;

    const citationSectionComponent = documentComponentFromData({
      id: this.guaranteedNewRandomHexidecimalKey(),
      documentCitations: {},
      unstyledContent: null,
      styling: null,
      ordinal: this.currentStartNode.ordinal,
      documentComponentType: 'DocumentCitationsSection',
      parentDocumentComponentId: null,
      firstChildDocumentComponentId: this.currentStartNode.id,
      lastChildDocumentComponentId: this.currentEndNode.id,
      previousDocumentComponentId: previousComponentId,
      nextDocumentComponentId: nextComponentId,
      isibuteDocument: this,
    });
    this.documentComponents[citationSectionComponent.id] = citationSectionComponent;

    if (previousComponentId && this.documentComponents[previousComponentId]) {
      this.documentComponents[previousComponentId].nextDocumentComponentId = citationSectionComponent.id;
    }
    if (nextComponentId && this.documentComponents[nextComponentId]) {
      this.documentComponents[nextComponentId].previousDocumentComponentId = citationSectionComponent.id;
    }
    let citationizedComponent;
    let newCurrentEndNode = null;

    if (this.cursorEndPosition <= 1) {
      return null;
    }

    if (styleCursorEndPosition < this.cursorStartPosition) {
      return null;
    }

    while (styleCursorEndPosition >= this.cursorStartPosition) {
      if (this.currentStartNode.id == styleEndNode.id) {
        citationizedComponent = this.currentStartNode.citationizedCopy({ parentDocumentComponentId: citationSectionComponent.id });

        this.currentStartNode = citationizedComponent;
        this.currentEndNode = newCurrentEndNode || citationizedComponent;
        delete this.documentComponents[citationizedComponent.id];
        citationSectionComponent.documentCitations[citationizedComponent.id] = citationizedComponent;

        return null;
      }

      if (styleEndNodeCursorPostion > 0) {
        citationizedComponent = styleEndNode.citationizedCopy({ parentDocumentComponentId: citationSectionComponent.id });

        if (!newCurrentEndNode) {
          newCurrentEndNode = citationizedComponent;
        }

        delete this.documentComponents[citationizedComponent.id];
        citationSectionComponent.documentCitations[citationizedComponent.id] = citationizedComponent;

        nextComponentBack = styleEndNode.getComponentForArrowLeft();

        styleCursorEndPosition = styleCursorEndPosition - styleEndNodeCursorPostion;
        styleEndNodeCursorPostion = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      } else {
        nextComponentBack = styleEndNode.getComponentForArrowLeft();

        styleCursorEndPosition = styleCursorEndPosition - styleEndNodeCursorPostion;
        styleEndNodeCursorPostion = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      }
    }

    const documentComponentsToIterate = Object.values(this.documentComponents);

    documentComponentsToIterate.forEach((documentComponent) => {
      if (documentComponent.ordinal > this.currentEndNode.ordinal) {
        documentComponent.ordinal = documentComponent.ordinal - ordinalDifference;
      }
    });
  };

  unorderedListizeCurrentComponents = () => {
    if (!this.currentStartNode) {
      throw new Error("Must select something to convert to unordered list.");
    }

    if (!this.currentEndNode) {
      throw new Error("Must select something to convert to unordered list.");
    }

    let styleCursorEndPosition = this.cursorEndPosition;
    let styleEndNodeCursorPostion = this.currentEndNodeCursorPosition - 1;
    let styleEndNode = this.currentEndNode;
    let nextComponentBack;
    const ordinalDifference = this.currentEndNode.ordinal - this.currentStartNode.ordinal;
    const previousComponentId = this.currentStartNode.previousDocumentComponentId;
    const nextComponentId = this.currentEndNode.nextDocumentComponentId;

    const unorderedListComponent = documentComponentFromData({
      id: this.guaranteedNewRandomHexidecimalKey(),
      documentUnorderedListItems: {},
      unstyledContent: null,
      styling: null,
      ordinal: this.currentStartNode.ordinal,
      documentComponentType: 'DocumentUnorderedList',
      parentDocumentComponentId: null,
      firstChildDocumentComponentId: this.currentStartNode.id,
      lastChildDocumentComponentId: this.currentEndNode.id,
      previousDocumentComponentId: previousComponentId,
      nextDocumentComponentId: nextComponentId,
      isibuteDocument: this,
    });
    this.documentComponents[unorderedListComponent.id] = unorderedListComponent;

    if (previousComponentId && this.documentComponents[previousComponentId]) {
      this.documentComponents[previousComponentId].nextDocumentComponentId = unorderedListComponent.id;
    }
    if (nextComponentId && this.documentComponents[nextComponentId]) {
      this.documentComponents[nextComponentId].previousDocumentComponentId = unorderedListComponent.id;
    }
    let listItemizedComponent;
    let newCurrentEndNode = null;

    if (this.cursorEndPosition <= 1) {
      return null;
    }

    if (styleCursorEndPosition < this.cursorStartPosition) {
      return null;
    }

    while (styleCursorEndPosition >= this.cursorStartPosition) {
      if (this.currentStartNode.id == styleEndNode.id) {
        listItemizedComponent = this.currentStartNode.unorderedListItemizedCopy({ parentDocumentComponentId: unorderedListComponent.id });

        this.currentStartNode = listItemizedComponent;
        this.currentEndNode = newCurrentEndNode || listItemizedComponent;
        delete this.documentComponents[listItemizedComponent.id];
        unorderedListComponent.documentUnorderedListItems[listItemizedComponent.id] = listItemizedComponent;

        return null;
      }

      if (styleEndNodeCursorPostion > 0) {
        listItemizedComponent = styleEndNode.unorderedListItemizedCopy({ parentDocumentComponentId: unorderedListComponent.id });

        if (!newCurrentEndNode) {
          newCurrentEndNode = listItemizedComponent;
        }

        delete this.documentComponents[listItemizedComponent.id];
        unorderedListComponent.documentUnorderedListItems[listItemizedComponent.id] = listItemizedComponent;

        nextComponentBack = styleEndNode.getComponentForArrowLeft();

        styleCursorEndPosition = styleCursorEndPosition - styleEndNodeCursorPostion;
        styleEndNodeCursorPostion = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      } else {
        nextComponentBack = styleEndNode.getComponentForArrowLeft();

        styleCursorEndPosition = styleCursorEndPosition - styleEndNodeCursorPostion;
        styleEndNodeCursorPostion = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      }
    }

    const documentComponentsToIterate = Object.values(this.documentComponents);

    documentComponentsToIterate.forEach((documentComponent) => {
      if (documentComponent.ordinal > this.currentEndNode.ordinal) {
        documentComponent.ordinal = documentComponent.ordinal - ordinalDifference;
      }
    });
  };

  insertImageComponent = ({ caption = '', altText = '', pendingImageData = null, pendingImageFileName = null, displaySizeVariant = 'large' }) => {
    if (!this.currentEndNode) {
      throw new Error("Must have a cursor position to insert image.");
    }

    if (this.currentEndNode.parentDocumentComponentId) {
      throw new Error("Cannot insert image inside a nested component (e.g., citation).");
    }

    const normalizedCaption = caption && !caption.startsWith('\u200b')
      ? '\u200b' + caption
      : (caption || '\u200b');

    const newImageComponent = documentComponentFromData({
      id: this.guaranteedNewRandomHexidecimalKey(),
      caption: normalizedCaption,
      altText,
      imagePath: null,
      pendingImageData,
      pendingImageFileName,
      unstyledContent: '\u200b',
      styling: [{ start: 0, end: 1, types: ['none'] }],
      ordinal: this.currentEndNode.ordinal + 1,
      documentComponentType: 'DocumentImage',
      displaySizeVariant,
      parentDocumentComponentId: null,
      firstChildDocumentComponentId: null,
      lastChildDocumentComponentId: null,
      previousDocumentComponentId: this.currentEndNode.id,
      nextDocumentComponentId: this.currentEndNode.nextDocumentComponentId,
      isibuteDocument: this,
    });

    Object.values(this.documentComponents).forEach((documentComponent) => {
      if (documentComponent.ordinal > this.currentEndNode.ordinal) {
        documentComponent.ordinal = documentComponent.ordinal + 1;
      }
    });

    if (this.currentEndNode.nextDocumentComponentId) {
      this.documentComponents[this.currentEndNode.nextDocumentComponentId].previousDocumentComponentId = newImageComponent.id;
    }
    this.documentComponents[this.currentEndNode.id].nextDocumentComponentId = newImageComponent.id;
    this.documentComponents[newImageComponent.id] = newImageComponent;

    this.cursorEndPosition = this.cursorEndPosition + 1;
    this.currentEndNodeCursorPosition = 1;
    this.currentEndNode = newImageComponent;
    this.setStartOfRangeToEnd();
  };

  deleteImageComponent = (componentId) => {
    const componentToDelete = this.documentComponents[componentId];

    if (!componentToDelete) {
      throw new Error(`Component with id ${componentId} not found.`);
    }

    if (componentToDelete.documentComponentType !== 'DocumentImage') {
      throw new Error("deleteImageComponent can only delete image components. Use a different method for other component types.");
    }

    const previousId = componentToDelete.previousDocumentComponentId;
    const nextId = componentToDelete.nextDocumentComponentId;

    if (previousId) {
      this.documentComponents[previousId].nextDocumentComponentId = nextId || null;
    }
    if (nextId) {
      this.documentComponents[nextId].previousDocumentComponentId = previousId || null;
    }

    Object.values(this.documentComponents).forEach((documentComponent) => {
      if (documentComponent.ordinal > componentToDelete.ordinal) {
        documentComponent.ordinal = documentComponent.ordinal - 1;
      }
    });

    delete this.documentComponents[componentId];

    if (this.currentStartNode?.id === componentId || this.currentEndNode?.id === componentId) {
      const newCurrentNode = previousId
        ? this.documentComponents[previousId]
        : (nextId ? this.documentComponents[nextId] : null);

      if (newCurrentNode) {
        this.currentStartNode = newCurrentNode;
        this.currentEndNode = newCurrentNode;
        this.currentStartNodeCursorPosition = newCurrentNode.unstyledContent.length;
        this.currentEndNodeCursorPosition = newCurrentNode.unstyledContent.length;
      }
    }
  };

  updateImageCaption = ({ componentId, caption }) => {
    const component = this.documentComponents[componentId];

    if (!component) {
      throw new Error(`Component with id ${componentId} not found.`);
    }

    if (component.documentComponentType !== 'DocumentImage') {
      throw new Error("updateImageCaption can only update image components.");
    }

    const normalizedCaption = caption && !caption.startsWith('\u200b')
      ? '\u200b' + caption
      : (caption || '\u200b');
    component.caption = normalizedCaption;
  };

  insertParagraphAfterImage = ({ componentId }) => {
    const imageComponent = this.documentComponents[componentId];

    if (!imageComponent) {
      throw new Error(`Component with id ${componentId} not found.`);
    }

    if (imageComponent.documentComponentType !== 'DocumentImage') {
      throw new Error("insertParagraphAfterImage can only be used with image components.");
    }

    const newParagraph = documentComponentFromData({
      id: this.guaranteedNewRandomHexidecimalKey(),
      unstyledContent: '\u200b',
      styling: [{ start: 0, end: 1, types: ['none'] }],
      ordinal: imageComponent.ordinal + 1,
      documentComponentType: 'DocumentParagraph',
      parentDocumentComponentId: null,
      firstChildDocumentComponentId: null,
      lastChildDocumentComponentId: null,
      previousDocumentComponentId: imageComponent.id,
      nextDocumentComponentId: imageComponent.nextDocumentComponentId,
      isibuteDocument: this,
    });

    Object.values(this.documentComponents).forEach((documentComponent) => {
      if (documentComponent.ordinal > imageComponent.ordinal) {
        documentComponent.ordinal = documentComponent.ordinal + 1;
      }
    });

    if (imageComponent.nextDocumentComponentId) {
      this.documentComponents[imageComponent.nextDocumentComponentId].previousDocumentComponentId = newParagraph.id;
    }
    imageComponent.nextDocumentComponentId = newParagraph.id;
    this.documentComponents[newParagraph.id] = newParagraph;

    this.currentStartNode = newParagraph;
    this.currentEndNode = newParagraph;
    this.currentStartNodeCursorPosition = 1;
    this.currentEndNodeCursorPosition = 1;
    this.cursorStartPosition = this.cursorEndPosition + 1;
    this.cursorEndPosition = this.cursorStartPosition;
  };

  applyStyle = (style) => {
    if (this.cursorEndPosition <= 1) return null;
    if (this.cursorEndPosition <= this.cursorStartPosition) return null;

    const activeTypes = this.getActiveStyleTypes();

    if (activeTypes.includes(style.type)) {
      return this.removeStyleFromRange(style);
    }

    return this.applyStyleToRange(style);
  };

  applyStyleToRange = (style) => {
    let styleCursorEndPosition = this.cursorEndPosition;
    let styleEndNodeCursorPostion = this.currentEndNodeCursorPosition - 1;
    let styleEndNode = this.currentEndNode;
    let nextComponentBack;

    const instruction = { types: [style.type] };
    if (style.href !== undefined) instruction.href = style.href;

    while (styleCursorEndPosition > this.cursorStartPosition) {
      if (this.currentStartNode.id == styleEndNode.id) {
        this.currentStartNode.applyStyle({
          start: this.currentStartNodeCursorPosition,
          end: styleEndNodeCursorPostion,
          ...instruction,
        });

        return null;
      }

      if (styleEndNodeCursorPostion > 0) {
        styleEndNode.applyStyle({
          start: 0,
          end: styleEndNodeCursorPostion,
          ...instruction,
        });

        const applyStyleCollection = this.getCollectionContainingNode(styleEndNode);
        applyStyleCollection[styleEndNode.id].styling = styleEndNode.styling;

        nextComponentBack = styleEndNode.getComponentForArrowLeft();

        styleCursorEndPosition = styleCursorEndPosition - styleEndNodeCursorPostion;
        styleEndNodeCursorPostion = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      } else {
        nextComponentBack = styleEndNode.getComponentForArrowLeft();

        styleCursorEndPosition = styleCursorEndPosition - styleEndNodeCursorPostion;
        styleEndNodeCursorPostion = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      }
    }
  };

  removeStyleFromRange = (style) => {
    let styleCursorEndPosition = this.cursorEndPosition;
    let styleEndNodeCursorPostion = this.currentEndNodeCursorPosition - 1;
    let styleEndNode = this.currentEndNode;
    let nextComponentBack;

    while (styleCursorEndPosition > this.cursorStartPosition) {
      if (this.currentStartNode.id == styleEndNode.id) {
        this.currentStartNode.removeStyle({
          start: this.currentStartNodeCursorPosition,
          end: styleEndNodeCursorPostion,
          types: [style.type],
        });

        return null;
      }

      if (styleEndNodeCursorPostion > 0) {
        styleEndNode.removeStyle({
          start: 0,
          end: styleEndNodeCursorPostion,
          types: [style.type],
        });

        const removeStyleCollection = this.getCollectionContainingNode(styleEndNode);
        removeStyleCollection[styleEndNode.id].styling = styleEndNode.styling;

        nextComponentBack = styleEndNode.getComponentForArrowLeft();

        styleCursorEndPosition = styleCursorEndPosition - styleEndNodeCursorPostion;
        styleEndNodeCursorPostion = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      } else {
        nextComponentBack = styleEndNode.getComponentForArrowLeft();

        styleCursorEndPosition = styleCursorEndPosition - styleEndNodeCursorPostion;
        styleEndNodeCursorPostion = nextComponentBack.unstyledContent.length;
        styleEndNode = nextComponentBack;
      }
    }
  };

  plainText = () => {
    const sortedComponents = Object.values(this.documentComponents)
      .sort((a, b) => a.ordinal - b.ordinal);

    const textParts = [];

    for (const component of sortedComponents) {
      if (component.documentComponentType === 'DocumentImage') {
        continue;
      }

      if (component.documentComponentType === 'DocumentCitationsSection') {
        const sortedCitations = Object.values(component.documentCitations)
          .sort((a, b) => a.ordinal - b.ordinal);

        for (const citation of sortedCitations) {
          textParts.push(citation.unstyledContent.replace(/\u200b/g, ''));
        }
        continue;
      }

      if (component.documentComponentType === 'DocumentUnorderedList') {
        const sortedItems = Object.values(component.documentUnorderedListItems)
          .sort((a, b) => a.ordinal - b.ordinal);

        for (const item of sortedItems) {
          textParts.push(item.unstyledContent.replace(/\u200b/g, ''));
        }
        continue;
      }

      if (component.documentComponentType === 'DocumentResumeContactHeader') {
        for (const child of Object.values(component.contactHeaderChildren)) {
          if (child.unstyledContent) {
            textParts.push(child.unstyledContent.replace(/\u200b/g, ''));
          }
        }
        continue;
      }

      textParts.push(component.unstyledContent.replace(/\u200b/g, ''));
    }

    return textParts.join('\n');
  };

  deepCopy = () => {
    const copy = new IsibuteDocument({ document: { documentComponents: {} }, leaveEmptyOnConstruction: true });

    copy.id = this.id;
    copy.documentManagerId = this.documentManagerId;
    copy.author = this.author;
    copy.publishedAt = this.publishedAt;
    copy.cursorStartPosition = this.cursorStartPosition;
    copy.cursorEndPosition = this.cursorEndPosition;
    copy.currentStartNodeCursorPosition = this.currentStartNodeCursorPosition;
    copy.currentEndNodeCursorPosition = this.currentEndNodeCursorPosition;
    copy.pendingStyleTypes = this.pendingStyleTypes ? [...this.pendingStyleTypes] : null;

    Object.entries(this.documentComponents).forEach(([key, value]) => {
      copy.documentComponents[key] = value.deepCopy();
      copy.documentComponents[key].isibuteDocument = copy;

      if (copy.documentComponents[key].documentCitations) {
        Object.values(copy.documentComponents[key].documentCitations).forEach((citation) => {
          citation.isibuteDocument = copy;
        });
      }

      if (copy.documentComponents[key].documentUnorderedListItems) {
        Object.values(copy.documentComponents[key].documentUnorderedListItems).forEach((item) => {
          item.isibuteDocument = copy;
        });
      }

      if (copy.documentComponents[key].contactHeaderChildren) {
        Object.values(copy.documentComponents[key].contactHeaderChildren).forEach((child) => {
          child.isibuteDocument = copy;
        });
      }
    });

    copy.currentStartNode = copy.getDocumentComponentByKeyPath({
      keyPath: this.currentStartNode?.keyPath()
    });

    copy.currentEndNode = copy.getDocumentComponentByKeyPath({
      keyPath: this.currentEndNode?.keyPath()
    });

    return copy;
  };
}

export default IsibuteDocument;
